import React, { useState, useEffect } from 'react';
import './ProjectCard.css';
import { FiExternalLink, FiGithub, FiX } from 'react-icons/fi';

function ProjectCard({ title, description, link, github, image, tags }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    // Calculate header height when component mounts
    const calculateHeaderHeight = () => {
      const header = document.querySelector('.header');
      if (header) {
        setHeaderHeight(header.offsetHeight);
      }
    };

    calculateHeaderHeight();
    
    // Recalculate on window resize
    window.addEventListener('resize', calculateHeaderHeight);
    
    return () => {
      window.removeEventListener('resize', calculateHeaderHeight);
    };
  }, []);

  const openModal = (e) => {
    e.preventDefault();
    setIsModalOpen(true);
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setIsModalOpen(false);
    // Restore body scroll
    document.body.style.overflow = 'unset';
  };

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isModalOpen) {
        closeModal();
      }
    };

    if (isModalOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isModalOpen]);

  return (
    <>
      <div className="project-card">
        {image && (
          <div
            className={`project-card__image${isModalOpen ? ' disabled' : ''}`}
            onClick={isModalOpen ? undefined : openModal}
          >
            <img src={image} alt={`${title} screenshot`} loading="lazy" />
            <div className="project-card__image-overlay">
              <span>View Project</span>
            </div>
          </div>
        )}

        <div className="project-card__content">
          <div className="project-card__header">
            <h3 className="project-card__title">{title}</h3>
            <div className="project-card__links">
              {github && (
                <a 
                  href={github} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  aria-label="GitHub repository"
                  className="project-card__icon-link"
                >
                  <FiGithub />
                </a>
              )}
              {link && (
                <a 
                  href={link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  aria-label="Live project"
                  className="project-card__icon-link"
                >
                  <FiExternalLink />
                </a>
              )}
            </div>
          </div>

          <p className="project-card__description">{description}</p>

          {tags && (
            <div className="project-card__tags">
              {tags.map((tag, index) => (
                <span key={index} className="project-card__tag">{tag}</span>
              ))}
            </div>
          )}

          {image && !isModalOpen && (
            <button
              className="project-card__link"
              onClick={openModal}
              aria-label={`View ${title}`}
            >
              View Screenshot
            </button>
          )}
        </div>
      </div>

      {/* Image Modal */}
      {isModalOpen && image && (
        <div 
          className="project-modal" 
          onClick={closeModal}
          style={{ paddingTop: `${headerHeight}px` }}
        >
          <div
            className="project-modal__content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="project-modal__close"
              onClick={closeModal}
              aria-label="Close modal"
              style={{ top: `${headerHeight + 20}px` }}
            >
              <FiX />
            </button>

            <img
              src={image}
              alt={`${title} - full view`}
              className="project-modal__image"
            />
          </div>
        </div>
      )}
    </>
  );
}

export default ProjectCard;